// How a job's photos and files show (the year run, 20 Oct): lists use a picture's thumbnail, the viewer
// its display copy, and "Open the original" the file itself. Until a copy is made the picture itself
// shows, except a HEIC, which says "Preparing photo". A camera RAW photo and an AVI are files to
// download, never shown in place; MMS wrappers and contact cards sit under "Other files".

import { photoFileName, photoNames, photoNote, photoTitle } from '@flowology/domain';

/** How the server says a file shows (attachments.list, customers.photos: `display`). */
export type Display = 'image' | 'preparing' | 'unshown' | 'raw' | 'video' | 'download' | 'pdf' | 'other';

export interface Shown {
  kind: string;
  contentType: string;
  status: string;
  url: string | null;
  originalUrl: string | null;
  thumbUrl?: string | null;
  annotated?: boolean;
  display?: Display;
  /** What arrived wasn't the file, and nothing sends it again by itself (#73, #76). */
  checkFailed?: boolean;
}

/** A file's category in a word, as the viewer and the job sheet's photos say it. */
export const CATEGORY_WORD: Record<string, string> = {
  before: 'Before',
  after: 'After',
  problem: 'The problem',
  equipment: 'Equipment',
  document: 'Document',
  other: 'Other',
};

/** The line on a file that came back to pending after its check (#73; the lead, 21 Oct). */
export const NOT_WHOLE = 'This file didn’t arrive whole. Upload it again.';

/** Its display, from the server, or from its kind and type for an answer from before it said one. */
export function displayOf(p: Pick<Shown, 'kind' | 'contentType' | 'display'>): Display {
  if (p.display) return p.display;
  if (p.kind === 'video') return 'video';
  if (p.kind === 'photo' || p.contentType.startsWith('image/')) return 'image';
  return p.contentType === 'application/pdf' ? 'pdf' : 'other';
}

/** A list's tile: a picture, a video to open, a file to open or download, or words while it waits. */
export type Tile =
  | { t: 'picture'; src: string }
  | { t: 'video' }
  | { t: 'file'; words: string; href: string | null }
  | { t: 'waiting'; words: string };

/** "AVI", "3GP": a video type's short name. */
const videoName = (type: string) =>
  type === 'video/x-msvideo' ? 'AVI' : (type.split('/')[1]?.replace(/^x-/, '').toUpperCase() ?? 'file');

export function tileOf(p: Shown): Tile {
  if (p.checkFailed && p.status !== 'verified') return { t: 'waiting', words: NOT_WHOLE };
  const d = displayOf(p);
  const href = p.originalUrl ?? p.url;
  switch (d) {
    case 'image': {
      const src = p.thumbUrl ?? p.url;
      return src
        ? { t: 'picture', src }
        : {
            t: 'waiting',
            // checking (#73): in the bucket, being read back before it's shown
            words:
              p.status === 'verified' ? 'No preview' : p.status === 'checking' ? 'Checking…' : 'Uploading…',
          };
    }
    case 'preparing':
      return { t: 'waiting', words: 'Preparing photo' };
    case 'unshown':
      return { t: 'file', words: 'Photo that can’t be shown here: open the original', href };
    case 'raw':
      return { t: 'file', words: 'Camera RAW photo: download', href };
    case 'video':
      return p.url ? { t: 'video' } : { t: 'waiting', words: 'Uploading…' };
    case 'download':
      return { t: 'file', words: `Video (${videoName(p.contentType)}): download`, href };
    case 'pdf':
      return { t: 'file', words: 'Open the PDF', href };
    default:
      return { t: 'file', words: 'Open the file', href };
  }
}

/** Kept with the job, but out of the photo strip: under "Other files". */
export const isOtherFile = (p: Pick<Shown, 'kind' | 'contentType' | 'display'>) => displayOf(p) === 'other';

/**
 * What the viewer shows: the original when asked, else the display copy (or the marked-up picture);
 * while a copy is still being made that's the original itself, so nothing waits on it but a HEIC.
 */
export function viewerSrc(p: Shown, original: boolean): string | null {
  if (displayOf(p) === 'preparing') return null;
  return original && p.originalUrl ? p.originalUrl : p.url;
}

/**
 * A file at the customer's address on a job filed under another customer (history fix 2): said by its
 * job number alone, never that customer's name. Null on the customer's own jobs.
 */
export function otherJobText(p: { otherCustomer?: boolean; jobNumber?: string }): string | null {
  if (!p.otherCustomer) return null;
  return p.jobNumber ? `On job #${p.jobNumber}, for another customer` : 'On a job for another customer';
}

/**
 * Each file's name in a list, by id (P-0161): its note, else a made-up name numbered in its category
 * over its whole job ("Before 2"), whatever a filter shows, so a name stays put as the choice changes.
 * The photo strip and the other files count apart; a list over several jobs counts each job apart.
 */
export function namesOf(
  files: readonly (Pick<Shown, 'kind' | 'contentType' | 'display'> & {
    id: string;
    category: string;
    caption: string | null;
    capturedAt?: string | null;
    jobId?: string;
  })[],
): Map<string, string> {
  const names = new Map<string, string>();
  for (const job of new Set(files.map((f) => f.jobId))) {
    const own = files.filter((f) => f.jobId === job);
    for (const part of [own.filter((f) => !isOtherFile(f)), own.filter(isOtherFile)])
      for (const [id, name] of photoNames(part)) names.set(id, name);
  }
  return names;
}

/**
 * What the viewer says of one (P-0161): its heading (its note, else "Before photo"), the note to edit
 * (empty for a caption that is only its file's name), and that file's own name for a quiet line.
 */
export function viewerWords(
  p: Pick<Shown, 'kind' | 'contentType' | 'display'> & { category: string },
  caption: string | null,
): { title: string; note: string | null; file: string | null } {
  const note = photoNote(caption);
  return { title: note ?? photoTitle(p), note, file: photoFileName(caption) };
}
